import { useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { OfficePosterSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** Screen-space pin (percent of viewport) for the clickable logo poster. */
export type PosterLinkPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/**
 * Bakes ONE poster's face texture: the AI-generated illustration filling the
 * top square, a solid accent caption band underneath with the crisp
 * Canvas2D-drawn headline/sub (never baked into the AI image itself, per the
 * platform's own poster-generator "hybrid render" philosophy — AI art +
 * real text layer, so the copy is always legible and typo-free).
 */
function drawPosterFace(img: HTMLImageElement, spec: OfficePosterSpec): THREE.CanvasTexture {
  const s = 768;
  const bandH = spec.sub ? 200 : 160;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s + bandH;
  const g = cv.getContext("2d")!;

  // Paper backing behind the illustration (visible as a thin light margin).
  g.fillStyle = "#faf8f2";
  g.fillRect(0, 0, cv.width, cv.height);
  g.drawImage(img, 0, 0, s, s);

  // Caption band — accent-tinted so it reads instantly as "poster", not a
  // random framed photo. Logo poster gets a slightly deeper teal for a
  // little extra visual weight (it's the clickable one).
  const bandColor = spec.linkUrl ? "#0f3f3a" : "#123a37";
  g.fillStyle = bandColor;
  g.fillRect(0, s, cv.width, bandH);
  g.fillStyle = "rgba(255,255,255,0.14)";
  g.fillRect(0, s, cv.width, 4);

  g.textAlign = "center";
  g.fillStyle = "#eafff8";
  g.font = "800 54px 'Segoe UI', Arial, sans-serif";
  const headY = spec.sub ? s + bandH * 0.36 : s + bandH * 0.52;
  wrapCentered(g, spec.headline, cv.width / 2, headY, cv.width - 72, 58);

  if (spec.sub) {
    g.font = "600 34px 'Segoe UI', Arial, sans-serif";
    g.fillStyle = "#8fd9cc";
    g.fillText(spec.sub, cv.width / 2, s + bandH * 0.82);
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Centred word-wrap, capped at 2 lines (3rd line would overflow the band). */
function wrapCentered(g: CanvasRenderingContext2D, text: string, cx: number, y: number, maxWidth: number, lineHeight: number) {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (g.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const capped = lines.slice(0, 2);
  const startY = y - ((capped.length - 1) * lineHeight) / 2;
  capped.forEach((l, i) => g.fillText(l, cx, startY + i * lineHeight));
}

/** One framed poster: illustration+caption plane + a thin extruded box frame
 *  (mirrors ChartBoard/DeskPackage's "real prop, not a flat decal" look). The
 *  clickable logo poster additionally projects a screen-space pin each frame
 *  (same technique as DeskPackage/ConferenceTv) so App can render a "visit
 *  cyberfrogsecurity.com" hotspot glued to it. */
function Poster({ spec, onLinkPin }: { spec: OfficePosterSpec; onLinkPin?: (pin: PosterLinkPin) => void }) {
  const { camera } = useThree();
  const [tex, setTex] = useState<THREE.CanvasTexture | null>(null);
  const anchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<PosterLinkPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onLinkPinRef = useRef(onLinkPin);
  onLinkPinRef.current = onLinkPin;

  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!alive) return;
      setTex(drawPosterFace(img, spec));
    };
    img.src = spec.imageUrl;
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.imageUrl, spec.headline, spec.sub]);
  useEffect(() => () => tex?.dispose(), [tex]);

  const width = spec.width ?? 0.46;
  const aspect = spec.sub ? (768 + 200) / 768 : (768 + 160) / 768;
  const height = width * aspect;
  const yaw = (spec.rotationDeg ?? 0) * DEG;
  const frameT = 0.022;
  const frameD = 0.02;

  useFrame(() => {
    if (!spec.linkUrl || !onLinkPinRef.current) return;
    const a = anchor.current;
    if (!a) return;
    a.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: PosterLinkPin = { xPct, yPct, visible: inFront && onScreen && distance < 6, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onLinkPinRef.current(pin);
    }
  });

  return (
    <group position={spec.position} rotation={[0, yaw, 0]}>
      <group ref={anchor}>
        {/* Frame: four thin boxes forming a border, sitting slightly proud of
            the wall so the poster face + a hair of shadow gap reads as
            "mounted", not "painted on". */}
        <mesh position={[0, height / 2 + frameT / 2, -frameD / 2]}>
          <boxGeometry args={[width + frameT * 2, frameT, frameD]} />
          <meshStandardMaterial color="#1c211f" roughness={0.55} metalness={0.15} />
        </mesh>
        <mesh position={[0, -height / 2 - frameT / 2, -frameD / 2]}>
          <boxGeometry args={[width + frameT * 2, frameT, frameD]} />
          <meshStandardMaterial color="#1c211f" roughness={0.55} metalness={0.15} />
        </mesh>
        <mesh position={[width / 2 + frameT / 2, 0, -frameD / 2]}>
          <boxGeometry args={[frameT, height, frameD]} />
          <meshStandardMaterial color="#1c211f" roughness={0.55} metalness={0.15} />
        </mesh>
        <mesh position={[-width / 2 - frameT / 2, 0, -frameD / 2]}>
          <boxGeometry args={[frameT, height, frameD]} />
          <meshStandardMaterial color="#1c211f" roughness={0.55} metalness={0.15} />
        </mesh>
        {/* Face */}
        <mesh position={[0, 0, 0.001]}>
          <planeGeometry args={[width, height]} />
          {tex ? (
            <meshStandardMaterial map={tex} roughness={0.85} />
          ) : (
            <meshStandardMaterial color="#e9e4d6" roughness={0.9} />
          )}
        </mesh>
      </group>
    </group>
  );
}

/** All wall posters for the current environment (reception nook + Episode-3
 *  lounge). Not gated on `scenario.walkable` — like DeskPackage/ConferenceTv
 *  these are real modelled props that simply sit wherever the scenario
 *  places them; only the one carrying `linkUrl` reports a click pin. */
export function OfficePosters({
  specs,
  onLinkPin,
}: {
  specs: OfficePosterSpec[];
  onLinkPin?: (pin: PosterLinkPin) => void;
}) {
  return (
    <group>
      {specs.map((spec) => (
        <Poster key={spec.id} spec={spec} onLinkPin={spec.linkUrl ? onLinkPin : undefined} />
      ))}
    </group>
  );
}
